import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  CourseraTierSubscriptions_AcquisitionType as AcquisitionType,
  OwnableProduct_BillingCycle,
  CourseraTierSubscriptions_PaymentCadence as PaymentCadence,
  CourseraTierSubscriptions_ProductType as ProductType,
} from '__generated__/graphql-types';

import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import QueryCourseraTierProductConfigurations from 'bundles/payments-common/api/QueryCourseraTierProductConfigurations.graphql';
import type {
  CourseraTierProductConfigurationsFragment,
  MonetaryAmountFragment,
  QueryCourseraTierProductConfigurationsQuery,
  QueryCourseraTierProductConfigurationsQueryVariables,
} from 'bundles/payments-common/api/__generated__/QueryCourseraTierProductConfigurations';
import ReactPriceDisplay from 'bundles/payments-common/components/ReactPriceDisplay';
import { OWNABLE_PRODUCT_PRODUCT_TYPE_MAP } from 'bundles/payments-common/hooks/useFindOwnableProducts';

export type AnnualWithFreeTrialProduct = {
  productItemId?: string | null;
  price?: CourseraTierProductConfigurationsFragment['price'] | null;
};

export type CourseraPlusProductAndPrice = {
  productItemId: string;
  price: MonetaryAmountFragment;
};

export const useQueryCourseraPlusAnnualWithFreeTrial = ({ skip = false }: { skip?: boolean }) => {
  const { data } = useQuery<
    QueryCourseraTierProductConfigurationsQuery,
    QueryCourseraTierProductConfigurationsQueryVariables
  >(QueryCourseraTierProductConfigurations, {
    variables: {
      acquisitionType: AcquisitionType.Standard,
      paymentCadence: PaymentCadence.AnnualSubscription,
      tier: ProductType.CourseraPlusSubscription,
    },
    context: { clientName: 'gatewayGql' },
    skip,
  });

  const courseraPlusAnnualWithFreeTrialProduct =
    data?.CourseraTierSubscriptionsQueries?.queryCourseraTierProductConfigurations?.find((product) => {
      if (product.fulfillmentConfiguration.paymentScheme.__typename === 'OwnableProduct_SubscriptionPaymentScheme') {
        return (
          product.fulfillmentConfiguration.paymentScheme.numberOfTrialDays > 0 &&
          product.ownableProduct.productType === OWNABLE_PRODUCT_PRODUCT_TYPE_MAP.CourseraPlusSubscription &&
          product.fulfillmentConfiguration.paymentScheme.billingCycle === OwnableProduct_BillingCycle.BillingCycleAnnual
        );
      }
      return false;
    });

  const formattedAnnualProduct: AnnualWithFreeTrialProduct = {
    productItemId: courseraPlusAnnualWithFreeTrialProduct?.fulfillmentConfiguration.legacyProductId?.productItemId,
    price: courseraPlusAnnualWithFreeTrialProduct?.price,
  };

  return {
    courseraPlusAnnualWithFreeTrialProduct: courseraPlusAnnualWithFreeTrialProduct ? formattedAnnualProduct : undefined,
  };
};

export const useGetCourseraPlusAnnualWithFreeTrialProduct = () => {
  const landingPageExtended = useLandingPageExtended();
  const {
    prices: courseraPlusPrices,
    userHasCourseraPlusFreeTrial,
    courseraPlusAnnualWithFreeTrialEpicVariant,
    isCourseraLiteOwner,
  } = landingPageExtended;
  const { primaryOriginalPrice: courseraPlusMonthlyPrice, secondaryPrice: courseraPlusAnnualNoFreeTrialPrice } =
    courseraPlusPrices;
  const { courseraPlusAnnualWithFreeTrialProduct } = useQueryCourseraPlusAnnualWithFreeTrial({
    skip: !['1', '2', '3'].includes(courseraPlusAnnualWithFreeTrialEpicVariant),
  });

  // For C Lite learners, there's an edge-case where the price of the existing C Lite
  // subscription is in a currency that does not match the currrency of their current
  // location at the point of attempting the upgrade. In this case, we should show the
  // annual price from the ProductPrices API, which is in the correct currency.
  // The ownable Products endpoint will be updated to support fetching my currency
  // in the future, but for now, this is a workaround.
  const formattedAnnualProductAndPrice = isCourseraLiteOwner
    ? {
        productItemId: courseraPlusAnnualNoFreeTrialPrice?.productItemId,
        price: {
          currencyCode: courseraPlusAnnualNoFreeTrialPrice?.currencyCode,
          value: String(courseraPlusAnnualNoFreeTrialPrice?.amount),
        },
      }
    : {
        productItemId: courseraPlusAnnualWithFreeTrialProduct?.productItemId,
        price: {
          currencyCode: courseraPlusAnnualWithFreeTrialProduct?.price?.amount?.currencyCode,
          value: courseraPlusAnnualWithFreeTrialProduct?.price?.amount?.value,
        },
      };
  const annualPriceAmount = Number(formattedAnnualProductAndPrice?.price?.value || 0);

  let annualDiscountPercent;
  let annualAmountAsMonthly;
  let annualPriceAsMonthlyDisplay;
  let monthlyPriceDisplay;
  let annualPriceDisplay;
  const isProductAndPriceValid =
    annualPriceAmount > 0 &&
    courseraPlusMonthlyPrice?.amount &&
    formattedAnnualProductAndPrice?.price?.currencyCode &&
    formattedAnnualProductAndPrice?.price?.value &&
    formattedAnnualProductAndPrice?.productItemId;

  if (isProductAndPriceValid) {
    annualAmountAsMonthly = Math.ceil(annualPriceAmount / 12);
    const totalCourseraPlusMonthlyPriceInAYear = courseraPlusMonthlyPrice.amount * 12;
    annualDiscountPercent = Math.ceil(
      ((totalCourseraPlusMonthlyPriceInAYear - annualPriceAmount) / totalCourseraPlusMonthlyPriceInAYear) * 100
    );

    annualPriceAsMonthlyDisplay = (
      <ReactPriceDisplay
        value={annualAmountAsMonthly}
        currency={formattedAnnualProductAndPrice.price.currencyCode}
        hideCurrencyCode={true}
      />
    );
    monthlyPriceDisplay = (
      <ReactPriceDisplay
        value={courseraPlusMonthlyPrice.amount}
        currency={courseraPlusMonthlyPrice.currencyCode}
        hideCurrencyCode={true}
      />
    );
    annualPriceDisplay = (
      <ReactPriceDisplay
        value={annualPriceAmount}
        currency={formattedAnnualProductAndPrice.price.currencyCode}
        hideCurrencyCode={true}
      />
    );
  }

  return {
    annualPriceDisplay,
    annualPriceAsMonthlyDisplay,
    annualAmountAsMonthly,
    annualDiscountPercent,
    courseraPlusAnnualProductAndPrice: formattedAnnualProductAndPrice as CourseraPlusProductAndPrice,
    monthlyPriceDisplay,
    courseraPlusMonthlyPrice,
    hasFreeTrial: userHasCourseraPlusFreeTrial,
  };
};
